import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';

import GadgetLoadError from 'components/GadgetLoadError';
import {
  GadgetStatus,
  RenderReadinessContext,
} from 'contextProviders/RenderReadinessProvider';

// One request's state. 'failed-handled': it failed, and the gadget shows its
// own error (it passed useLockedDebouncedEffect an errorHandler)
export type GadgetRequestStatus =
  | 'loading'
  | 'done'
  | 'failed'
  | 'failed-handled';

interface GadgetStatusContextType {
  reportRequest: (
    requestId: string,
    status: GadgetRequestStatus | undefined,
  ) => void;
}

// A safe no-op outside a gadget (Explore, builders' previews, app-level
// providers): requests report wherever they run
export const GadgetStatusContext = React.createContext<GadgetStatusContextType>(
  {
    reportRequest: () => {},
  },
);

/**
 * For a gadget's request that doesn't go through useLockedDebouncedEffect:
 * returns a function to report its state to the gadget's boundary. Clears the
 * report on unmount.
 */
export const useReportGadgetRequest = () => {
  const { reportRequest } = useContext(GadgetStatusContext);
  const requestId = useId();
  useEffect(
    () => () => reportRequest(requestId, undefined),
    [reportRequest, requestId],
  );
  return useCallback(
    (status: GadgetRequestStatus | undefined) =>
      reportRequest(requestId, status),
    [reportRequest, requestId],
  );
};

const ErrorOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  background-color: var(--color-bg-primary);
  border-radius: inherit;
`;

const toGadgetStatus = (
  requests: GadgetRequestStatus[],
): GadgetStatus | undefined => {
  if (requests.includes('loading')) {
    return 'loading';
  }
  if (requests.includes('failed') || requests.includes('failed-handled')) {
    return 'failed';
  }
  return requests.length > 0 ? 'done' : undefined;
};

/**
 * Collects the state of a gadget's data requests (useLockedDebouncedEffect
 * reports each one), so a gadget whose request fails shows an error rather
 * than its loading skeleton forever, and a scheduled render knows when every
 * gadget is done. Wraps a canvas card, or a Composite's element; needs a
 * positioned ancestor for its error overlay. Its children stay mounted under
 * the overlay, so a later request (new filters) can clear it.
 */
const GadgetStatusBoundary = ({
  id,
  name,
  children,
}: {
  id: string;
  name: string;
  children: React.ReactNode;
}) => {
  const [requests, setRequests] = useState<Record<string, GadgetRequestStatus>>(
    {},
  );
  const { reportGadget, removeGadget } = useContext(RenderReadinessContext);

  const reportRequest = useCallback(
    (requestId: string, status: GadgetRequestStatus | undefined) => {
      setRequests((current) => {
        if (current[requestId] === status) {
          return current;
        }
        if (status === undefined) {
          if (!(requestId in current)) {
            return current;
          }
          const { [requestId]: _removed, ...rest } = current;
          return rest;
        }
        return { ...current, [requestId]: status };
      });
    },
    [],
  );

  const statuses = Object.values(requests);
  const status = toGadgetStatus(statuses);
  // A gadget showing its own error doesn't need the overlay
  const isShowingOverlay = statuses.includes('failed');

  useEffect(() => {
    // A gadget with no requests yet (or none at all, like an image) isn't
    // holding a render up; the print screens' skeleton check covers one still
    // getting ready to request
    reportGadget({ id, name, status: status ?? 'done' });
  }, [id, name, reportGadget, status]);
  useEffect(() => () => removeGadget(id), [id, removeGadget]);

  const value = useMemo(() => ({ reportRequest }), [reportRequest]);

  return (
    <GadgetStatusContext.Provider value={value}>
      {children}
      {isShowingOverlay && (
        <ErrorOverlay>
          <GadgetLoadError message="Couldn't load this data" />
        </ErrorOverlay>
      )}
    </GadgetStatusContext.Provider>
  );
};

export default GadgetStatusBoundary;
